import { useEffect, useRef, useState } from 'react';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import {
  axios,
  SitterRate,
  SitterRateSuggestion,
  SitterRateSuggestionService,
  SitterService,
} from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const MIN_RATE = 8;
const NOT_WILLING_ID = 8;
const SITTER_HOLDING_BOY_BG =
  'https://assets.urbansitter.com/static/img/backgrounds/sitter-holding-boy.webp';

const doesChildCare = (sitter: {
  experiences?: { infant?: { id?: number }; toddler?: { id?: number }; school?: { id?: number } };
}): boolean => {
  const exp = sitter.experiences;
  if (!exp) return false;
  return (
    (!!exp.infant?.id && exp.infant.id !== NOT_WILLING_ID) ||
    (!!exp.toddler?.id && exp.toddler.id !== NOT_WILLING_ID) ||
    (!!exp.school?.id && exp.school.id !== NOT_WILLING_ID)
  );
};

const doesPetCare = (sitter: { willSitDog?: boolean; willSitCat?: boolean; willSitOtherPet?: boolean }): boolean =>
  !!sitter.willSitDog || !!sitter.willSitCat || !!sitter.willSitOtherPet;

// Angular's getCareGivingLabel builds "children, seniors or pets" with commas + "or"
// rather than plain "or or or". Mirror that here.
const careGivingLabel = (
  sitter: {
    experiences?: { infant?: { id?: number }; toddler?: { id?: number }; school?: { id?: number } };
    willSitDog?: boolean;
    willSitCat?: boolean;
    willSitOtherPet?: boolean;
    willSitSenior?: boolean;
  },
  plural: boolean,
): string => {
  const parts: string[] = [];
  if (doesChildCare(sitter)) parts.push(plural ? 'children' : 'child');
  if (sitter.willSitSenior) parts.push(plural ? 'seniors' : 'senior');
  if (doesPetCare(sitter)) parts.push(plural ? 'pets' : 'pet');
  if (parts.length === 0) return plural ? 'people' : 'person';
  if (parts.length === 1) return parts[0];
  const head = parts.slice(0, -1);
  const last = parts[parts.length - 1];
  return `${head.join(', ')} or ${last}`;
};

export const Rates = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { stores } = useDependencies();
  const sitter = stores.sitter.sitter;

  const [rates, setRates] = useState<SitterRate[]>(() =>
    (sitter?.rates ?? []).map((r) => ({
      ...r,
      minimumRate: r.minimumRate === 0 ? (null as unknown as number) : r.minimumRate,
    })),
  );
  const [suggested, setSuggested] = useState<SitterRateSuggestion | null>(null);
  const [showExtraRates, setShowExtraRates] = useState(false);
  const [maxKids, setMaxKids] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const firstRateRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    document.title = 'UrbanSitter - Your Rates';
    // Preserve the legacy autoFocus-on-mount behaviour while keeping lint happy.
    firstRateRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!sitter?.id || !sitter.address?.zip) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterRateSuggestionService.get({
          params: { kids: 1, sitterId: sitter.id, zip: sitter.address.zip },
        });
        setSuggested(resp.data?.data ?? null);
      } catch (err) {
        console.warn('[Rates] suggested rate load failed', err);
      }
    };
    void load();
  }, [sitter?.id, sitter?.address?.zip]);

  const showRatesSection = sitter
    ? doesChildCare(sitter) || doesPetCare(sitter) || !!sitter.willSitSenior
    : false;

  const validRate = (v: number | null | undefined): boolean =>
    v == null || (typeof v === 'number' && v >= MIN_RATE);

  const hasValidRates = ((): boolean => {
    const primary = rates[0]?.minimumRate;
    if (primary == null || !validRate(primary)) return false;
    if (!showRatesSection) return true;
    return (
      validRate(rates[1]?.minimumRate) &&
      validRate(rates[2]?.minimumRate) &&
      validRate(rates[3]?.minimumRate)
    );
  })();

  const canSubmit = !submitting && hasValidRates;

  const updateRate = (index: number, value: string): void => {
    const parsed = value === '' ? (null as unknown as number) : Number(value);
    setRates((prev) => prev.map((r, i) => (i === index ? { ...r, minimumRate: parsed } : r)));
  };

  const submit = async (): Promise<void> => {
    if (!sitter?.id || !canSubmit) return;
    setSubmitting(true);
    try {
      await SitterService.patch(sitter.id, {
        id: sitter.id,
        rates: rates.map((r) => ({
          ...r,
          minimumRate: r.minimumRate ?? 0,
        })),
        ...(showExtraRates && maxKids ? { maxKids } : {}),
      } as never);
      await stores.sitter.refreshSitter?.();
      navigate('/sitter/registration/about-me');
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data?.errors?.[0]?.message) {
        toast.error(e.response.data.errors[0].message);
      } else {
        toast.error(t('pages.rates.submitFailed'));
      }
      setSubmitting(false);
    }
  };

  if (!sitter?.id) {
    return (
      <div className='container py-8'>
        <p className='text-neutral-600'>{t('common.loading')}</p>
      </div>
    );
  }

  const singularLabel = careGivingLabel(sitter, false);
  const pluralLabel = careGivingLabel(sitter, true);
  const suggestionText = suggested?.rate
    ? (suggested.isDefaultRate
        ? t('pages.rates.suggestionDefault', { amount: suggested.rate, label: singularLabel })
        : t('pages.rates.suggestionArea', { amount: suggested.rate, label: singularLabel }))
    : null;

  const rateField = (
    index: number,
    id: string,
    label: string,
    placeholder: string,
  ): JSX.Element => {
    const rate = rates[index];
    const value = rate?.minimumRate == null ? '' : String(rate.minimumRate);
    const invalid = value !== '' && !validRate(rate?.minimumRate);
    return (
      <div className='mb-5'>
        <label htmlFor={id} className='block mb-1 text-sm'>
          {label}
        </label>
        <div className='flex items-center'>
          <span className='inline-flex items-center px-3 py-2 bg-neutral-100 border border-r-0 border-neutral-300 rounded-l'>
            $
          </span>
          <input
            ref={index === 0 ? firstRateRef : undefined}
            id={id}
            type='tel'
            inputMode='numeric'
            maxLength={2}
            pattern='[0-9]*'
            placeholder={placeholder}
            className={`form-control flex-1 border rounded-r px-3 py-2 ${
              invalid ? 'border-red-500' : 'border-neutral-300'
            }`}
            value={value}
            onChange={(e): void => updateRate(index, e.target.value)}
          />
        </div>
        {invalid && (
          <i className='inline-block mt-1 text-sm text-red-600'>
            {t('pages.rates.minRate', { min: MIN_RATE })}
          </i>
        )}
      </div>
    );
  };

  return (
    <div className='relative overflow-hidden md:min-h-[700px]'>
      <div
        aria-hidden='true'
        className='hidden md:block absolute inset-0 bg-no-repeat bg-right-top bg-[length:40%] lg:bg-[length:50%] xl:bg-[position:center_right] xl:bg-[length:clamp(0px,60%,1000px)] pointer-events-none'
        style={{ backgroundImage: `url(${SITTER_HOLDING_BOY_BG})` }}
      />
      <div className='relative container py-8 max-w-md md:ml-0 md:mr-auto md:pl-8 md:pr-0'>
      <h1 className='text-2xl font-semibold mb-4'>{t('pages.rates.heading')}</h1>

      {suggestionText && <div className='text-sm mb-6'>{suggestionText}</div>}

      {rateField(
        0,
        'min-rate-1',
        t('pages.rates.minRateLabel'),
        t('pages.rates.minRatePlaceholder'),
      )}

      {showRatesSection && (
        <div className='mb-5 text-center'>
          <h4 className='font-medium'>
            <Button
              buttonType={ButtonType.tertiary}
              className='inline-flex items-center gap-2 cursor-pointer font-medium bg-transparent border-0 p-0'
              onClick={(): void => setShowExtraRates((s) => !s)}
            >
              <span>
                {showExtraRates
                  ? t('pages.rates.hideExtraRates')
                  : t('pages.rates.showExtraRates')}
              </span>
              <FontAwesomeIcon
                icon={showExtraRates ? faChevronUp : faChevronDown}
                className='text-lg'
              />
            </Button>
          </h4>
        </div>
      )}

      {showExtraRates && showRatesSection && (
        <>
          {rateField(
            1,
            'min-rate-2',
            t('pages.rates.minRateForTwo', { label: pluralLabel }),
            t('pages.rates.twoPlaceholder', { label: pluralLabel }),
          )}
          {rateField(
            2,
            'min-rate-3',
            t('pages.rates.minRateForThree', { label: pluralLabel }),
            t('pages.rates.threePlaceholder', { label: pluralLabel }),
          )}
          {rateField(
            3,
            'min-rate-4',
            t('pages.rates.minRateForFour', { label: pluralLabel }),
            t('pages.rates.fourPlaceholder', { label: pluralLabel }),
          )}

          <div className='mb-5'>
            <label htmlFor='max-num-kids' className='block mb-1 text-sm'>
              {t('pages.rates.maxLabel', { label: pluralLabel })}
            </label>
            <select
              id='max-num-kids'
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
              value={maxKids}
              onChange={(e): void => setMaxKids(e.target.value === '' ? '' : Number(e.target.value))}
              required
            >
              <option value='' disabled>
                {t('pages.rates.maxPlaceholder')}
              </option>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      <div className='text-center mt-12'>
        <Button
          id='submit-location'
          buttonType={ButtonType.primary}
          onClick={submit}
          disabled={!canSubmit}
        >
          {t('pages.rates.continue')}
        </Button>
        <div className='flex justify-center gap-2 mt-5'>
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <span
              key={i}
              className={`w-2 h-2 rounded-full inline-block ${
                i === 5 ? 'bg-sapphire' : 'border border-neutral-300'
              }`}
            />
          ))}
        </div>
      </div>
      </div>
    </div>
  );
});
